import { FILL_GRID, FILL_CELL, createFill } from './strokes';
import { paintStrokes } from './raster';

// The paint bucket.
//
// Rasterise the drawing so far onto a FILL_GRID-sized canvas, flood outward
// from the tapped cell across every cell that is (near enough) the same
// colour as the one tapped, and hand back that region as runs. The region is
// what gets stored - see createFill for why it is not re-flooded elsewhere.
//
// "Near enough" matters: anti-aliasing puts intermediate colours along every
// line's edge. A tolerance lets the flood reach right up to a line without
// leaking through it, since the line's own body is far outside it.

const TOLERANCE = 96; // summed |dR|+|dG|+|dB|

const near = (data, i, [r, g, b]) => (
  Math.abs(data[i] - r) + Math.abs(data[i + 1] - g) + Math.abs(data[i + 2] - b) <= TOLERANCE
);

/**
 * The pure part: pixels in, runs out. `data` is RGBA, row-major, `size` wide.
 * Returns [] when the seed is off-grid.
 */
export const floodRuns = (data, size, seedX, seedY) => {
  if (seedX < 0 || seedY < 0 || seedX >= size || seedY >= size) return [];
  const at = (x, y) => (y * size + x) * 4;
  const seedIdx = at(seedX, seedY);
  const seed = [data[seedIdx], data[seedIdx + 1], data[seedIdx + 2]];
  const seen = new Uint8Array(size * size);
  const stack = [seedX, seedY];
  seen[seedY * size + seedX] = 1;
  while (stack.length) {
    const y = stack.pop();
    const x = stack.pop();
    const neighbours = [[x + 1, y], [x - 1, y], [x, y + 1], [x, y - 1]];
    for (const [nx, ny] of neighbours) {
      if (nx < 0 || ny < 0 || nx >= size || ny >= size) continue;
      const cell = ny * size + nx;
      if (seen[cell]) continue;
      if (!near(data, at(nx, ny), seed)) continue;
      seen[cell] = 1;
      stack.push(nx, ny);
    }
  }
  const runs = [];
  for (let y = 0; y < size; y += 1) {
    let start = -1;
    for (let x = 0; x <= size; x += 1) {
      const on = x < size && seen[y * size + x];
      if (on && start < 0) start = x;
      if (!on && start >= 0) { runs.push([y, start, x - start]); start = -1; }
    }
  }
  return runs;
};

/**
 * Flood fill at canvas units (x, y) over `strokes`, in `color`. Returns the
 * fill stroke to append, or null when nothing would change (tapping a spot
 * already that colour, or a browser with no canvas).
 */
export const floodFill = (strokes, x, y, color, makeCanvas = () => document.createElement('canvas')) => {
  const canvas = makeCanvas();
  canvas.width = FILL_GRID;
  canvas.height = FILL_GRID;
  let ctx = null;
  try { ctx = canvas.getContext?.('2d'); } catch { ctx = null; }
  if (!ctx) return null;
  paintStrokes(ctx, strokes, FILL_GRID);
  const { data } = ctx.getImageData(0, 0, FILL_GRID, FILL_GRID);
  const cx = Math.min(FILL_GRID - 1, Math.floor(x / FILL_CELL));
  const cy = Math.min(FILL_GRID - 1, Math.floor(y / FILL_CELL));
  const runs = floodRuns(data, FILL_GRID, cx, cy);
  if (runs.length === 0) return null;
  return createFill(color, runs);
};
